import React, { useState, useEffect, useCallback } from "react";
import "./UploadPage.css";
import { API_BASE } from "../api/config";

const UploadPage = () => {
    const [csvData, setCsvData] = useState(null);
    const [loading, setLoading] = useState(false); // ✅ NEU: Loading-State
    const [batchResult, setBatchResult] = useState(null);
    const [batchError, setBatchError] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
  
    // Sitzungs-ID aus LocalStorage abrufen
    const sessionId = localStorage.getItem("sessionId");
  
    // 🔄 Backend-Daten abrufen
    const fetchSessionData = async () => {
      if (!sessionId) {
        console.warn("Keine Sitzung vorhanden.");
        return;
      }
  
      setLoading(true); // ✅ Lade-Animation AN
      try {
        const response = await fetch(`${API_BASE}/session/data`, {
          method: "GET",
          headers: { "Session-ID": sessionId },
        });
  
        if (response.ok) {
          const data = await response.json();
          if (data.raw_data && data.raw_data.length > 0) {
            setCsvData(data);
            console.log("Daten aus der Sitzung geladen:", data);
          }
        } else {
          console.warn("Keine gespeicherten Daten gefunden.");
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der Sitzungsdaten:", error);
      } finally {
        setLoading(false); // ✅ Lade-Animation AUS
      }
    };
  
    // 📤 Einzelne CSV hochladen
    const handleSingleCsvUpload = async (file) => {
      if (!file) {
        alert("Bitte wählen Sie eine CSV-Datei aus.");
        return;
      }
  
      if (!sessionId) {
        alert("Keine Sitzung aktiv. Bitte starten Sie eine neue Sitzung.");
        return;
      }
  
      setLoading(true); // ✅ Lade-Animation AN
      const formData = new FormData();
      formData.append("file", file);
  
      try {
        const response = await fetch(`${API_BASE}/csv/upload`, {
          method: "POST",
          body: formData,
          headers: { "Session-ID": sessionId },
        });
  
        if (response.ok) {
          const data = await response.json();
          setCsvData(data);
          console.log("CSV erfolgreich hochgeladen:", data);
        } else {
          alert("Fehler beim Hochladen der CSV-Datei.");
        }
      } catch (error) {
        console.error("Fehler beim Hochladen der CSV:", error);
      } finally {
        setLoading(false); // ✅ Lade-Animation AUS
      }
    };
  
    // 📤 Mehrere CSVs hochladen
    const handleMultipleCsvUpload = async (files) => {
      if (!files || files.length === 0) {
        alert("Bitte wählen Sie mindestens eine CSV-Datei oder einen Ordner aus.");
        return;
      }

      if (!sessionId) {
        alert("Keine Sitzung aktiv. Bitte starten Sie eine neue Sitzung.");
        return;
      }

      setLoading(true);
      setBatchError(null);
      setBatchResult(null);

      try {
        // Erlaube Ordnerauswahl: filtere auf .csv
        const onlyCsv = Array.from(files).filter(f => f.name.toLowerCase().endsWith(".csv"));
        if (onlyCsv.length === 0) {
          alert("Keine CSV-Dateien gefunden.");
          setLoading(false);
          return;
        }

        const formData = new FormData();
        for (const f of onlyCsv) {
          // webkitRelativePath hilft, Ordnerstruktur zu übertragen (falls vorhanden)
          formData.append("files", f, f.webkitRelativePath || f.name);
        }

        const response = await fetch(`${API_BASE}/csv/upload-batch`, {
          method: "POST",
          headers: { "Session-ID": sessionId },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setBatchResult(data);
      } catch (error) {
        console.error("Fehler beim Batch-Upload:", error);
        setBatchError(String(error));
      } finally {
        setLoading(false);
      }
    };

  // Drag & Drop Handler für einzelne CSV
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const csvFiles = files.filter(f => f.name.toLowerCase().endsWith('.csv'));
    
    if (csvFiles.length === 1) {
      handleSingleCsvUpload(csvFiles[0]);
    } else if (csvFiles.length > 1) {
      handleMultipleCsvUpload(csvFiles);
    } else {
      alert("Bitte nur CSV-Dateien ablegen.");
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSessionData(); // Prüft nur, ob Daten vorhanden sind, ohne automatisch eine Sitzung zu erstellen
  }, []);

  // Prüfen ob Ergebnisse vorhanden sind
  const hasResults = csvData || batchResult;

  return (
    <div className={`upload-page ${hasResults ? 'has-results' : ''}`}>
      {loading && ( // ✅ NEUES LOADING POPUP
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Daten werden geladen...</p>
        </div>
      )}

      <div className="content-container">
        {/* Linke Seite (Buttons) */}
        <div className="left-container">
          <div className="button-container">
            <label 
              className={`styled-button drop-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              Einzelne CSV
              <input
                type="file"
                onChange={(e) => handleSingleCsvUpload(e.target.files[0])}
                accept=".csv"
                className="hidden-input"
              />
            </label>

            <label 
              className={`styled-button drop-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              Mehrere CSVs / Ordner
              <input
                type="file"
                multiple
                webkitdirectory=""
                mozdirectory=""
                directory=""
                onChange={(e) => handleMultipleCsvUpload(Array.from(e.target.files))}
                accept=".csv"
                className="hidden-input"
              />
            </label>
          </div>
        </div>

        {/* Trennlinie */}
        <div className="divider"></div>

        {/* Rechte Seite (Ergebnisse) */}
        <div className="right-container">
          {csvData && (
            <div className="analysis-results">
              <h2>CSV-Analyse</h2>
              <p><strong>Kontonummer:</strong> {csvData.konto || "Keine Kontonummer gefunden"}</p>
              <p><strong>Zeilenanzahl:</strong> {csvData.total_rows}</p>
              <p><strong>Zeitraum:</strong> {csvData.oldest_date || "N/A"} - {csvData.newest_date || "N/A"}</p>
            </div>
          )}

          {batchResult && (
            <div className="multi-analysis-results">
              <h2>Batch-Ergebnis</h2>
              <p>
                <strong>Verarbeitet:</strong> {batchResult.count?.processed ?? (batchResult.processed?.length || 0)}
                {" "}|{" "}
                <strong>Übersprungen:</strong> {batchResult.count?.skipped ?? (batchResult.skipped?.length || 0)}
              </p>

              {Array.isArray(batchResult.processed) && batchResult.processed.length > 0 && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                  <table
                    aria-label="Verarbeitete Dateien"
                    style={{
                      fontSize: "12px",
                      borderCollapse: "collapse",
                      width: "min(1000px, 95%)",
                      background: "var(--table-bg, #fff)",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>#</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>Datei</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>Kontonummer</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>Zeilen</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>Von</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>Bis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchResult.processed.map((item, index) => (
                        <tr key={index}>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{index + 1}</td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee", maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.filename || item.name || `#${index + 1}`}
                          </td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{item.konto || "—"}</td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{item.total_rows ?? "—"}</td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{item.oldest_date || "—"}</td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{item.newest_date || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {Array.isArray(batchResult.skipped) && batchResult.skipped.length > 0 && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
                  <table
                    aria-label="Übersprungene Dateien"
                    style={{
                      fontSize: "12px",
                      borderCollapse: "collapse",
                      width: "min(1000px, 95%)",
                      background: "var(--table-bg, #fff)",
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>#</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>Datei</th>
                        <th style={{ padding: "6px 8px", borderBottom: "1px solid #ddd", textAlign: "left" }}>Grund</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchResult.skipped.map((s, index) => (
                        <tr key={index}>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{index + 1}</td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee", maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.filename || s.name || `#${index + 1}`}
                          </td>
                          <td style={{ padding: "6px 8px", borderBottom: "1px solid #eee" }}>{s.reason || "Unbekannt"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {batchError && (
                <p className="error" style={{ textAlign: "center" }}>Fehler: {batchError}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPage;